import { useTranslation } from "react-i18next";
import { RefreshCwIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useRunSync, useSyncStatus } from "@/hooks/use-admin";
import { cn } from "@/lib/utils";

/**
 * Admin-only manual sync. Pulls fresh numbers for everyone rather than just
 * refetching this browser's cache, so it is the fix for "the board looks stale".
 *
 * The server still enforces its own minimum interval, so leaning on this cannot
 * turn into a burst of Meta API calls.
 */
export function RefreshButton() {
  const { t } = useTranslation();
  const { data: status } = useSyncStatus();
  const runSync = useRunSync();
  const busy = runSync.isPending || (status?.isRunning ?? false);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon-sm"
          disabled={busy}
          onClick={() => runSync.mutate(false)}
          aria-label={t("range.refresh")}
        >
          <RefreshCwIcon className={cn(busy && "animate-spin motion-reduce:animate-none")} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {busy ? t("range.syncing") : t("range.refreshTooltip")}
      </TooltipContent>
    </Tooltip>
  );
}
